/**
 * NuGuard AI - Invitation Banner
 *
 * Shown when the logged-in user has a pending invitation to another tenant.
 * Allows accepting (migrates tenant) or dismissing.
 */

import React, { useEffect, useState } from 'react';
import { Mail, ArrowRight, X } from 'lucide-react';
import { invitationsApi, tokenManager, PendingInvitationCheck } from '../services/api';
import { cn } from '../lib/utils';

interface InvitationBannerProps {
  onAccepted: () => void;
}

const InvitationBanner: React.FC<InvitationBannerProps> = ({ onAccepted }) => {
  const [pending, setPending] = useState<PendingInvitationCheck | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    invitationsApi.checkPending()
      .then(setPending)
      .catch(() => {}); // silently ignore if endpoint unavailable
  }, []);

  if (!pending?.has_pending || dismissed) return null;

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const result = await invitationsApi.accept();
      // Store new tokens via TokenManager
      tokenManager.setTokens(result.access_token, result.refresh_token, result.expires_in);
      onAccepted();
    } catch {
      setAccepting(false);
    }
  };

  return (
    <div className={cn(
      "flex items-center justify-between gap-4 px-4 py-3",
      "bg-primary-50 dark:bg-primary-500/10 border-b border-primary-200 dark:border-primary-500/20"
    )}>
      <div className="flex items-center gap-3">
        <Mail className="w-5 h-5 text-primary-600 dark:text-primary-400 shrink-0" />
        <p className="text-sm text-primary-800 dark:text-primary-300">
          You've been invited to join another organization as <strong>{pending.invitation?.role}</strong>.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={handleAccept}
          disabled={accepting}
          className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors",
            "bg-primary-600 hover:bg-primary-700 text-white",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          {accepting ? 'Accepting...' : (
            <>Accept <ArrowRight className="w-3.5 h-3.5" /></>
          )}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1.5 text-primary-400 hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default InvitationBanner;
